import React, { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../../adapters/api-client/api';
import ErrorState from '../../../commodity/components/ui/ErrorState';
import {
  getConsentsForChild,
  grantConsent,
  revokeConsent,
} from '../../../core/identity-access/api/consent';
import { RecordingConsentNotice, getRecordingConsent } from '../api/enrollments';

/**
 * The consent type for family playback (backend: verticals/iqmax/domain/consent-types.ts).
 * Separate from capture: every class is recorded, watching it back is the
 * guardian's own, optional, revocable choice.
 */
export const RECORDING_PLAYBACK_CONSENT = 'IQMAXX_RECORDING_PLAYBACK';

interface Props {
  studentId: string;
  learnerName: string;
  /** Whether any listed class is waiting on this grant (recording state PLAYBACK_NOT_GRANTED). */
  needsGrant: boolean;
  /** Called after a grant or revoke is stored, so the list is read again from the server. */
  onChanged: () => void;
}

const card: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 10,
  padding: 14,
  background: '#f8f9fb',
  marginBottom: 16,
};

const kidModeRefusal = (error: unknown) =>
  error instanceof ApiError && error.body?.code === 'KID_MODE_ACTIVE';

/** The notice changed while it was on screen: the server hands back the current one. */
const noticeChanged = (error: unknown): RecordingConsentNotice | undefined => {
  if (!(error instanceof ApiError) || error.body?.code !== 'RECORDING_CONSENT_CHANGED') {
    return undefined;
  }
  const fresh = error.body?.recordingConsent as RecordingConsentNotice | undefined;
  return fresh && typeof fresh.version === 'string' ? fresh : undefined;
};

/**
 * PRT-12-FR-03 — grant or withdraw playback for one learner after enrolling.
 *
 * Every enrollment before Sprint 3 has no playback grant, and checkout is the
 * only other place it can be given. Here the guardian reads the same versioned
 * notice checkout shows (served by the API, one copy of the words) and records
 * the grant through the consent API under that version. The server decides
 * what becomes playable: after a change the list is read again, nothing is
 * flipped locally. Granting and withdrawing are refused in kid mode by the
 * server; the screen says so plainly.
 */
const PlaybackPermission: React.FC<Props> = ({ studentId, learnerName, needsGrant, onChanged }) => {
  const [granted, setGranted] = useState<boolean | null>(null);
  const [notice, setNotice] = useState<RecordingConsentNotice | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      const [consents, text] = await Promise.all([
        getConsentsForChild(studentId),
        getRecordingConsent(),
      ]);
      // A grant counts only under the notice in force, as on the server: one
      // given to earlier words is offered again, with the current ones.
      setGranted(
        !!text &&
          (consents?.consents ?? []).some(
            (c) =>
              c.consentType === RECORDING_PLAYBACK_CONSENT &&
              c.granted === true &&
              c.version === text.version,
          ),
      );
      setNotice(text ?? null);
    } catch {
      // Unknown is not granted, and an unread notice cannot be agreed to.
      setGranted(false);
      setNotice(null);
    }
  }, [studentId]);

  useEffect(() => {
    setGranted(null);
    setError(null);
    load();
  }, [load]);

  const change = async (grant: boolean) => {
    setSaving(true);
    setError(null);
    try {
      if (grant) {
        if (!notice) return;
        await grantConsent(studentId, RECORDING_PLAYBACK_CONSENT, notice.version);
      } else {
        await revokeConsent(studentId, RECORDING_PLAYBACK_CONSENT);
      }
      await load();
      onChanged();
    } catch (err) {
      const fresh = noticeChanged(err);
      // Show the words now in force; the guardian decides again on those.
      if (fresh) setNotice(fresh);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  if (granted === null) return null;
  if (!granted && !needsGrant) return null;

  const headingId = `playback-permission-${studentId}`;
  return (
    <section aria-labelledby={headingId} style={card}>
      <h2 id={headingId} style={{ fontSize: 16, margin: '0 0 6px' }}>
        Ver las grabaciones de {learnerName}
      </h2>

      {granted ? (
        <>
          <p style={{ margin: '0 0 10px', fontSize: 14 }}>
            Diste permiso para ver desde tu cuenta las grabaciones de las clases a las que{' '}
            {learnerName} asistió. Puedes retirarlo cuando quieras.
          </p>
          <button type="button" onClick={() => change(false)} disabled={saving}>
            {saving ? 'Guardando…' : 'Retirar el permiso'}
          </button>
        </>
      ) : notice ? (
        <>
          <p style={{ margin: '0 0 6px', fontSize: 14 }}>{notice.playback}</p>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#666' }}>
            Aviso de grabación, versión {notice.version}
          </p>
          <button type="button" onClick={() => change(true)} disabled={saving}>
            {saving ? 'Guardando…' : `Permitir ver las grabaciones de ${learnerName}`}
          </button>
        </>
      ) : (
        <p style={{ margin: 0, fontSize: 14 }}>
          No pudimos cargar el aviso de grabación. Vuelve a intentarlo más tarde.
        </p>
      )}

      {error ? (
        kidModeRefusal(error) ? (
          <p role="alert" style={{ margin: '10px 0 0', color: '#8f2323', fontSize: 14 }}>
            Esto lo tiene que hacer un adulto, fuera del espacio del alumno.
          </p>
        ) : noticeChanged(error) ? (
          <p role="alert" style={{ margin: '10px 0 0', color: '#8f2323', fontSize: 14 }}>
            El aviso de grabación cambió. Léelo de nuevo y, si estás de acuerdo, vuelve a dar el
            permiso.
          </p>
        ) : (
          <div style={{ marginTop: 10 }}>
            <ErrorState action="guardar el permiso" error={error} compact />
          </div>
        )
      ) : null}
    </section>
  );
};

export default PlaybackPermission;
